import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Help as HelpIcon,
    ExpandMore,
    QuestionAnswer,
    Book,
    VideoLibrary,
    Feedback,
    Email,
    Construction
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Help = () => {
    const navigate = useNavigate();

    const helpCategories = [
        {
            icon: <Book />,
            title: 'Getting Started',
            description: 'Learn how to use TilBuzz effectively'
        },
        {
            icon: <VideoLibrary />,
            title: 'Video Tutorials',
            description: 'Watch step-by-step guides'
        },
        {
            icon: <QuestionAnswer />,
            title: 'FAQs',
            description: 'Find answers to common questions'
        },
        {
            icon: <Feedback />,
            title: 'Contact Support',
            description: 'Get help from our support team'
        }
    ];

    const faqs = [
        {
            question: "How do I start learning with TilBuzz?",
            answer: "Begin by exploring the Short Stories section to find content that matches your reading level. Click on words you don't know to add them to your personal dictionary."
        },
        {
            question: "What are the different content types?",
            answer: "TilBuzz offers Short Stories for quick reading practice, Long Posts for in-depth articles, and Video Posts for listening and comprehension skills."
        },
        {
            question: "How does the word tracking system work?",
            answer: "When you click on unfamiliar words while reading, they're automatically added to your personal dictionary. You can review them later using the flashcard system."
        },
        {
            question: "Can I track my learning progress?",
            answer: "Yes! Your Home dashboard shows your reading history, learned words, and progress over time with interactive charts and statistics."
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 6, textAlign: 'center' }}>
                <HelpIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 600,
                        color: 'primary.main'
                    }}
                >
                    Help & Support
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    We're here to help you get the most out of TilBuzz
                </Typography>
            </Box>

            {/* Help Categories */}
            <Grid container spacing={3} sx={{ mb: 6 }}>
                {helpCategories.map((category, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4
                                }
                            }}
                        >
                            <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        backgroundColor: 'primary.light',
                                        color: 'primary.contrastText',
                                        display: 'inline-flex',
                                        mb: 2
                                    }}
                                >
                                    {category.icon}
                                </Box>
                                <Typography variant="h6" fontWeight="600" gutterBottom>
                                    {category.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {category.description}
                                </Typography>
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <Construction color="action" fontSize="small" />
                                    <Typography variant="caption" color="text.secondary">
                                        Coming Soon
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* FAQ Section */}
            <Paper elevation={1} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
                <Typography variant="h4" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                    Frequently Asked Questions
                </Typography>
                
                {faqs.map((faq, index) => (
                    <Accordion key={index} sx={{ mb: 1 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{ 
                                '& .MuiAccordionSummary-content': {
                                    my: 1
                                }
                            }}
                        >
                            <Typography variant="h6" fontWeight="600">
                                {faq.question}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                {faq.answer}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Paper>

            {/* Contact Section */}
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                <Email sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" fontWeight="600" gutterBottom>
                    Still need help?
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
                    Our support team is here to assist you with any questions or issues.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={<Email />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5
                        }}
                    >
                        Contact Support
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/profile')}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5
                        }}
                    >
                        Back to Home
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default Help; 